import { isEqual } from "es-toolkit";
import { is } from "immutable";
import { type Getter, atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";
import { Logger } from "logging";

import { $accessTokenFunc } from "../../authenication";
import { LOCAL_WORKSPACE_NAME } from "../../constants";
import { $docsApi, DocsApiError } from "../../docsapi/docsapi.atom";
import type { UserWithRelation } from "../../docsapi/docsapi.types";
import { $remoteWorkspaceFromDocsApi } from "../../document-metadata/document-metadata.atom";
import type { Workspace } from "../../workspaces/workspace.types";
import {
	$localInvites,
	$localUserPermissions,
	$pendingPermissionsAtom,
} from "./pending-permissions.atom";
import type { Invitation, PermRelation, PermissionUpdate } from "./permissions.types";

const logger = new Logger("permissions.atom");

const EVERYONE = "RESERVED_EVERYONE";

const fetchPermissions = async (
	get: Getter,
	documentId: string | undefined,
	initialWorkspaceName: string | undefined
) => {
	// Get all the data first.
	const permissions = new Map<string, UserWithRelation>();
	const { getAccessTokenSilently } = get($accessTokenFunc);
	if (!getAccessTokenSilently) {
		return {
			isPublished: false,
			hasPublishedPassword: false,
			userPermissions: new Map(),
			workspacePermission: undefined,
			invites: [],
		};
	}
	const token = await getAccessTokenSilently();
	if (!token) {
		return {
			isPublished: false,
			hasPublishedPassword: false,
			userPermissions: new Map(),
			workspacePermission: undefined,
			invites: [],
		};
	}

	let workspaceName = initialWorkspaceName;

	if (!documentId || !workspaceName) {
		return {
			isPublished: false,
			hasPublishedPassword: false,
			userPermissions: new Map(),
			workspacePermission: undefined,
			invites: [],
		};
	}

	if (workspaceName === LOCAL_WORKSPACE_NAME) {
		// lookup the document metadata to see
		// if there is a document remote.
		const workspace = (await get($remoteWorkspace(documentId))) ?? LOCAL_WORKSPACE_NAME;
		// if it is still local, return empty permissions
		// as this is still an offline document.
		if (workspace === LOCAL_WORKSPACE_NAME || workspace === undefined) {
			return {
				isPublished: false,
				hasPublishedPassword: false,
				userPermissions: new Map(),
				workspacePermission: undefined,
				invites: [],
			};
		}

		workspaceName = workspace.name;
	}

	const docsApi = get($docsApi);

	// Query the data

	const roles = await docsApi.document.permissions.queryRolesForDocument(
		{
			workspaceName,
			documentId,
		},
		token
	);

	logger.debug("Fetched permissions", { workspaceName, documentId, roles });
	const invites =
		(await docsApi.document.permissions.queryInvites(
			{
				workspaceName,
				documentId,
			},
			token
		)) || [];

	logger.debug("Fetched invites", { workspaceName, documentId, invites });
	let workspacePermission = undefined;

	// Check specifically in the reader role for the public user
	const readers = roles.relation.reader || [];
	const publicReader = readers.find((s) => s.id === "*");
	const isPublished = !!publicReader;
	const hasPublishedPassword = publicReader?.has_publish_password ?? false;

	for (const role in roles.relation) {
		if (role === "workspace") {
			continue;
		}

		if (role !== "owner" && role !== "collaborator" && role !== "reader") {
			logger.debug("skipping role", { role });
			continue;
		}

		const subjects = roles.relation[role] || [];
		subjects.forEach((subject) => {
			if (subject.type.includes("/user")) {
				const user = roles.users[subject.id];
				if (!user) {
					logger.warn("User not found in roles.users", { subjectId: subject.id });
					return;
				}
				permissions.set(subject.id, {
					...user,
					relation: role,
				});
				return;
			}

			workspacePermission = {
				relation: role,
				workspace_id: subject.id, // Workspace IDs
			};
		});
	}

	return {
		// I don't know if this is what the frontend wants.
		// but thought i show an example.
		isPublished,
		hasPublishedPassword,
		// I am storing the workspace
		// information here so we know that these permissions are related to
		// this workspace/documentId pair.
		userPermissions: permissions,
		workspacePermission,
		invites,
	};
};

type PermissionsParams = { documentId: string; workspaceName: string | undefined };

const $initialDocumentPermissions = atomFamily((params: PermissionsParams) => {
	const permissionsAtom = atomWithRefresh(
		async (
			get
		): Promise<{
			isPublished: boolean;
			hasPublishedPassword: boolean;
			userPermissions: Map<string, UserWithRelation>;
			workspacePermission: { relation: PermRelation; workspace_id: string } | undefined;
			invites: Array<Invitation>;
		}> => {
			return await fetchPermissions(get, params.documentId, params.workspaceName);
		}
	);
	permissionsAtom.debugLabel = `initialDocumentPermissions/${params.documentId}`;
	return permissionsAtom;
}, isEqual);

// Draft atom for tracking if publish password has been set (for optimistic updates)
const $draftHasPublishedPassword = atomFamily((_documentId: string | undefined) => {
	const draftAtom = atom(null as boolean | null);
	draftAtom.debugPrivate = true;
	return draftAtom;
});

// Read-only: get document permissions
export const $documentPermissions = atomFamily((params: PermissionsParams) => {
	const { documentId } = params;
	const permissionsAtom = atom(async (get) => {
		const base = await get($initialDocumentPermissions(params));
		// Check for draft override of hasPublishedPassword (for optimistic updates)
		const draftHasPassword = get($draftHasPublishedPassword(documentId));
		if (draftHasPassword !== null) {
			return {
				...base,
				hasPublishedPassword: draftHasPassword,
			};
		}
		return base;
	});
	permissionsAtom.debugLabel = `documentPermissions/${documentId}`;
	return permissionsAtom;
}, isEqual);

// Write-only: update document permissions
export const $updateDocumentPermissions = atom(
	null,
	async (
		get,
		set,
		params: {
			documentId: string;
			workspaceName: string;
			updates: Map<string, PermissionUpdate>;
		}
	) => {
		const { documentId, workspaceName, updates } = params;

		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			return;
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			return;
		}

		if (!workspaceName || workspaceName === LOCAL_WORKSPACE_NAME) {
			throw new Error("Cannot update permissions for local workspace");
		}

		const docsApi = get($docsApi);

		logger.debug("Updating document permissions", {
			documentId,
			workspaceName,
			updates,
		});

		let updateCount = 0;
		for (const update of updates.values()) {
			logger.debug(`Updating permission #${++updateCount}`, { update });
			try {
				switch (update.type) {
					case "Workspace":
						await docsApi.document.permissions.updateWorkspace(
							{
								workspaceName,
								documentId,
								relation: update.relation as PermRelation,
								type: update.action,
							},
							token
						);
						break;
					case "Username":
						await docsApi.document.permissions.update(
							{
								workspaceName,
								documentId,
								relation: update.relation as PermRelation,
								user: update.user,
								type: update.action,
							},
							token
						);
						break;
					case "Email":
						await docsApi.document.permissions.updateInvite(
							{
								workspaceName,
								documentId,
								email: update.inviteEmail,
								type: update.action,
								relation: update.relation as PermRelation,
							},
							token
						);
						break;
				}
			} catch (error) {
				logger.error("Error updating permission", {
					error,
					update,
				});
				throw error;
			}
		}

		// Okay refetch the permissions after update!
		logger.debug("Updating document permissions successful. refetch permissions", {
			documentId,
			workspaceName,
			updates,
		});

		set($initialDocumentPermissions({ documentId, workspaceName }));
		const updated = await get($initialDocumentPermissions({ documentId, workspaceName }));
		set($localInvites, updated.invites);
		set($localUserPermissions, updated.userPermissions);
		set($pendingPermissionsAtom, new Map());
	}
);
$updateDocumentPermissions.debugLabel = "updateDocumentPermissions";

const $draftIsDocumentPublished = atomFamily((_documentId: string | undefined) => {
	const draftAtom = atom(null as boolean | null);
	draftAtom.debugPrivate = true;
	return draftAtom;
});

// Read-only: check if document is published
export const $isDocumentPublished = atomFamily((documentId: string | undefined) => {
	const publishedAtom = atom(async (get) => {
		const cachedPublished = get($draftIsDocumentPublished(documentId));
		if (cachedPublished !== null) {
			return cachedPublished;
		}
		if (!documentId) {
			return false;
		}

		const workspace = await get($remoteWorkspace(documentId));
		if (!workspace) {
			return false;
		}
		const workspaceName = workspace.name;

		const permissions = await get(
			$documentPermissions({
				documentId,
				workspaceName,
			})
		);
		if (!permissions) {
			return false;
		}

		// I am unsure if we want to seperate this or not. but for now, published means
		// either isPublished or hasPublishedPassword is true
		return permissions.isPublished || permissions.hasPublishedPassword;
	});
	publishedAtom.debugLabel = `isDocumentPublished/${documentId}`;
	return publishedAtom;
});

export const $isDocumentPublishedWithPassword = atomFamily((documentId: string | undefined) => {
	const publishedAtom = atom(async (get) => {
		const cachedPublished = get($draftIsDocumentPublished(documentId));
		if (cachedPublished !== null) {
			return cachedPublished;
		}
		if (!documentId) {
			return false;
		}

		const workspace = await get($remoteWorkspace(documentId));
		if (!workspace) {
			return false;
		}
		const workspaceName = workspace.name;

		const permissions = await get(
			$documentPermissions({
				documentId,
				workspaceName,
			})
		);
		if (!permissions) {
			return false;
		}

		return permissions.hasPublishedPassword;
	});
	publishedAtom.debugLabel = `isDocumentPublishedWithPassword/${documentId}`;
	return publishedAtom;
});

// Write-only: set document published status
export const $setDocumentPublished = atom(
	null,
	async (
		get,
		set,
		params: {
			isPublished: boolean;
			documentId: string;
			workspace: Workspace;
		}
	) => {
		const { isPublished, documentId, workspace } = params;
		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token available, cannot publish document");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			throw new Error("No access token available, cannot publish document");
		}

		const type = isPublished ? "grant" : "revoke";

		await docsApi.document.permissions.updatePublicAccess(
			{
				workspaceName: workspace.name,
				documentId,
				relation: "reader",
				type,
				loggedInUsersOnly: false,
			},
			token
		);

		set($draftIsDocumentPublished(documentId), isPublished);
	}
);
$setDocumentPublished.debugLabel = "setDocumentPublished";

// Write-only: set document published with password protection
export const $setDocumentPublishedWithPassword = atom(
	null,
	async (
		get,
		set,
		params: {
			documentId: string;
			workspace: Workspace;
			password: string;
		}
	) => {
		const { documentId, workspace, password } = params;
		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token available, cannot publish document");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			throw new Error("No access token available, cannot publish document");
		}

		await docsApi.document.permissions.publishWithPassword(
			{
				workspaceName: workspace.name,
				documentId,
				password,
			},
			token
		);

		set($draftIsDocumentPublished(documentId), true);
	}
);
$setDocumentPublishedWithPassword.debugLabel = "setDocumentPublishedWithPassword";

/**
 * Update the publish mode of an already-published document.
 * Can switch between public (no password) and password-protected modes.
 */
export const $updatePublishMode = atom(
	null,
	async (
		get,
		set,
		params: {
			documentId: string;
			workspace: Workspace;
			mode: "public" | "password";
			password?: string;
		}
	) => {
		const { documentId, workspace, mode, password } = params;
		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);
		if (!getAccessTokenSilently) {
			throw new Error("No access token available, cannot update publish mode");
		}
		const token = await getAccessTokenSilently();
		if (!token) {
			throw new Error("No access token available, cannot update publish mode");
		}

		if (mode === "password") {
			if (!password || password.length < 8) {
				throw new Error("Password must be at least 8 characters");
			}
			await docsApi.document.permissions.publishWithPassword(
				{
					workspaceName: workspace.name,
					documentId,
					password,
				},
				token
			);
			set($draftHasPublishedPassword(documentId), true);
		} else {
			// Switch to public mode - this grants public read access without password
			await docsApi.document.permissions.updatePublicAccess(
				{
					workspaceName: workspace.name,
					documentId,
					relation: "reader",
					type: "grant",
					loggedInUsersOnly: false,
				},
				token
			);
			set($draftHasPublishedPassword(documentId), false);
		}

		// Refresh permissions to get the latest state
		set($initialDocumentPermissions({ documentId, workspaceName: workspace.name }));

		// Clear the optimistic draft state so the refreshed permissions take over
		set($draftHasPublishedPassword(documentId), null);
	}
);
$updatePublishMode.debugLabel = "updatePublishMode";

/**
 * Check if user has share permission for the document
 */
export const $hasSharePermission = atomFamily((documentId: string | undefined) => {
	const permissionAtom = atom(async (get) => {
		try {
			if (!documentId) {
				return false;
			}
			const workspace = await get($remoteWorkspace(documentId));
			if (!workspace) {
				return false;
			}
			const workspaceName = workspace.name;

			const docsApi = get($docsApi);
			const { getAccessTokenSilently } = get($accessTokenFunc);
			if (!getAccessTokenSilently) {
				return false;
			}
			const token = await getAccessTokenSilently();
			if (!token) {
				return false;
			}

			return await docsApi.document.permissions.check(
				{
					workspaceName,
					documentId,
					permission: "share",
				},
				token
			);
		} catch (error) {
			if (error instanceof DocsApiError) {
				logger.error("DocsApiError details", {
					resp: error.resp,
					error: error.error,
				});
			} else {
				logger.error("Failed to check share permission", { error });
			}
			return false;
		}
	});
	permissionAtom.debugLabel = `hasSharePermission/${documentId}`;
	return permissionAtom;
});

const $draftRemoteWorkspace = atomFamily((_documentId: string | undefined) => {
	const draftAtom = atom(undefined as Workspace | undefined);
	draftAtom.debugPrivate = true;
	return draftAtom;
});

// Read-only: get remote workspace for document
export const $remoteWorkspace = atomFamily((documentId: string | undefined) => {
	const workspaceAtom = atom(async (get) => {
		if (!documentId) {
			return undefined;
		}
		// Check to see if shared the document first
		const draft = get($draftRemoteWorkspace(documentId));
		if (draft) {
			return draft;
		}

		// Otherwise, get from docsapi
		const remoteWorkspace = await get($remoteWorkspaceFromDocsApi({ documentId }));
		return remoteWorkspace;
	});
	workspaceAtom.debugLabel = `remoteWorkspace/${documentId}`;
	return workspaceAtom;
});

// Write-only: set remote workspace for document
export const $setRemoteWorkspace = atom(
	null,
	(_get, set, params: { documentId: string; workspace: Workspace }) => {
		set($draftRemoteWorkspace(params.documentId), params.workspace);
	}
);
$setRemoteWorkspace.debugLabel = "setRemoteWorkspace";
